import axios from 'axios'

export type AuthUser = {
	user_id: number
	login: string
	is_super_admin: boolean
	can_manage_accounts: boolean
	permissions: string[]
}

export type ManagedUser = {
	id: number
	login: string
	full_name: string | null
	position: string | null
	is_super_admin: boolean
	can_manage_accounts: boolean
	is_active: boolean
	permissions: string[]
	created_at: string
	updated_at: string
}

export type AuditEvent = {
	id: number
	created_at: string
	login: string | null
	event_type: string
	page_slug: string | null
	ip: string
	user_agent: string
}

type ApiEnvelope<T> = {
	success: boolean
	data: T
	error?: { code?: string; message?: string }
}

const tokenStorageKey = 'auth_portal_token'
const base =
	import.meta.env.VITE_API_BASE ||
	(import.meta.env.PROD
		? 'https://service-api.lkbot.zabota.tech/auth-api/v1'
		: 'https://service-api-dev.lkbot.zabota.tech/auth-api/v1')

export const api = axios.create({
	baseURL: base,
})

api.interceptors.request.use((config) => {
	const token = localStorage.getItem(tokenStorageKey)
	if (token) {
		config.headers = config.headers ?? {}
		config.headers.Authorization = `Bearer ${token}`
	}
	return config
})

export function saveToken(token: string | null): void {
	if (!token) {
		localStorage.removeItem(tokenStorageKey)
		return
	}
	localStorage.setItem(tokenStorageKey, token)
}

export async function login(loginValue: string, password: string): Promise<{ token: string; user: AuthUser }> {
	const res = await api.post<ApiEnvelope<{ token: string; user: AuthUser }>>('/auth/login', {
		login: loginValue,
		password,
	})
	return res.data.data
}

export async function me(): Promise<AuthUser> {
	const res = await api.get<ApiEnvelope<AuthUser>>('/auth/me')
	return res.data.data
}

export async function logout(): Promise<void> {
	await api.post('/auth/logout')
}

export async function logPageVisit(pageSlug: string): Promise<void> {
	await api.post('/auth/events/page-visit', { page_slug: pageSlug })
}

export async function listUsers(): Promise<ManagedUser[]> {
	const res = await api.get<ApiEnvelope<ManagedUser[]>>('/auth/users')
	return res.data.data
}

export async function createUser(payload: {
	login: string
	full_name?: string
	position?: string
	password: string
	permissions: string[]
	can_manage_accounts: boolean
	is_super_admin: boolean
}): Promise<void> {
	await api.post('/auth/users', payload)
}

export async function listPositions(): Promise<string[]> {
	const res = await api.get<ApiEnvelope<{ positions: string[] }>>('/auth/users/positions')
	return res.data.data.positions
}

export async function updateUserProfile(
	userId: number,
	payload: { full_name?: string; position?: string; password?: string; password_confirm?: string }
): Promise<void> {
	await api.patch(`/auth/users/${userId}/profile`, payload)
}

export async function changeUserPassword(userId: number, password: string): Promise<void> {
	await api.put(`/auth/users/${userId}/password`, { password })
}

export async function setUserActive(userId: number, isActive: boolean): Promise<void> {
	await api.patch(`/auth/users/${userId}/active`, { is_active: isActive })
}

export async function listAuditEvents(limit = 1000, login?: string): Promise<AuditEvent[]> {
	const qs = new URLSearchParams({ limit: String(limit) })
	if (login) {
		qs.set('login', login)
	}
	const res = await api.get<ApiEnvelope<AuditEvent[]>>(`/auth/events?${qs.toString()}`)
	return res.data.data
}
